import React from "react";
import { Box, Paper, Typography, Chip, Divider, Stack } from "@mui/material";
import {
  GrupoPergunta,
  Pergunta,
  Resposta,
} from "../../../../types/checklistData";

type Props = {
  tituloChecklist: string;
  checklistId?: number;
  grupos: GrupoPergunta[];
};

export default function ChecklistPreview({
  tituloChecklist,
  checklistId,
  grupos,
}: Props) {
  const gruposOrdenados = [...grupos].sort(
    (a, b) => a.ordem_checklist_grupo - b.ordem_checklist_grupo
  );

  return (
    <Paper elevation={1} sx={{ p: 3, mt: 3 }}>
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
          {tituloChecklist || "Checklist (sem título)"}
        </Typography>
        {typeof checklistId === "number" && (
          <Chip size="small" label={`ID ${checklistId}`} />
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />

      {/* SEM GRUPOS */}
      {gruposOrdenados.length === 0 ? (
        <Typography color="text.secondary">
          Nenhum grupo adicionado ainda. Clique em “Novo Grupo” para começar.
        </Typography>
      ) : (
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        gruposOrdenados.map((g, idxG) => {
          const perguntasOrdenadas: Pergunta[] = [...g.perguntas].sort(
            (a, b) => a.ordem_checklist_pergunta - b.ordem_checklist_pergunta
          );

          return (
            <Box key={g.id} mb={3}>
              {/* TÍTULO DO GRUPO */}
              <Box display="flex" alignItems="baseline" gap={1} mb={1}>
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{ fontWeight: 600 }}
                >
                  {`${g.ordem_checklist_grupo}. ${g.descricao_checklist_grupo}`}
                </Typography>
                {g.legenda && (
                  <Chip size="small" variant="outlined" label="Legenda" />
                )}
                {g.ajuda_checklist_grupo && (
                  <Chip size="small" variant="outlined" label="Ajuda" />
                )}
                {g.rodape_checklist_grupo && (
                  <Chip size="small" variant="outlined" label="Rodapé" />
                )}
              </Box>

              {/* SEM PERGUNTAS */}
              {perguntasOrdenadas.length === 0 ? (
                <Typography color="text.secondary" ml={1.5}>
                  Nenhuma pergunta neste grupo.
                </Typography>
              ) : (
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                perguntasOrdenadas.map((p, idxP) => {
                  const respostasOrdenadas: Resposta[] = [...p.respostas]; 
                  return (
                    <Box key={p.id} ml={1.5} mt={1.5}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        flexWrap="wrap"
                      >
                        <Typography variant="h6" component="h4">
                          {`${p.ordem_checklist_pergunta}. ${p.descricao_checklist_pergunta}`}
                        </Typography>
                        {p.obrigatorio_checklist_pergunta && (
                          <Chip
                            size="small"
                            color="error"
                            label="Obrigatória"
                          />
                        )}
                      </Box>

                      {/* RESPOSTAS */}
                      {respostasOrdenadas.length === 0 ? (
                        <Typography color="text.secondary" ml={1}>
                          Sem respostas cadastradas.
                        </Typography>
                      ) : (
                        <Stack spacing={0.5} mt={0.5} ml={1}>
                          {respostasOrdenadas.map((r) => (
                            <Typography key={r.id} color="text.secondary">
                              • {r.descricao_checklist_resposta}{" "}
                              <Typography
                                component="span"
                                color="text.disabled"
                              >
                                ({r.valor_checklist_respostas})
                              </Typography>
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
          );
        })
      )}
    </Paper>
  );
}
